import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {Box, Divider, List, ListItem, ListItemButton, ListItemText, Skeleton, Stack, Typography} from "@mui/material";
import Flag from './Flag';
import PanelListItem from "./PanelListItem";
import {countries} from "../services/Countries";
let keyboardIndex = 0;
const moveFocus = (ref, difference) => {
    let children = [...ref.current.children];
    children = children.flatMap((child) => {
        if (child.children?.length < 1) {
            return []
        }
        return [child.children[0]];
    });
    if (children.length === 0) return;
    let nextIndex = keyboardIndex + difference;
    if (nextIndex > children.length - 1) {
        nextIndex = 0;
    } else if (nextIndex < 0) {
        nextIndex = children.length - 1;
    }
    children[nextIndex].focus();
    keyboardIndex = nextIndex;
}
export default function ProfilePanel({
                                         isViewing,
                                         searchParams,
                                         favoriteSearchIndex,
                                         handleClose,
                                         setSearchParams,
                                         updateUser,
                                         updateUserSubscription,
                                         data,
                                         index,
                                         nullMessage,
                                         open, subscriptions, loadingUserData
                                     }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current || !isViewing) return;
        const handleKeyDown = (event) => {
            if (!ref.current || !isViewing || !open) return;
            if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                event.preventDefault();
                moveFocus(ref, -1);
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                event.preventDefault();
                moveFocus(ref, 1);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        if (open && isViewing) {
            moveFocus(ref, 0)
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [ref, isViewing, open]);


    const setFavoriteSearchIndex = useCallback((itemIndex, setLoading) => {
        updateUser({'favoriteSearchIndex': itemIndex}, setLoading, "Favorite updated");
    }, [updateUser]);

    const setUserSubscription = useCallback((item, setLoading) => {
        updateUserSubscription(item, setLoading, "Subscription updated");
    }, [updateUserSubscription]);

    function getListItem(item, itemIndex, subs) {
        let display = null;
        if (index === 0) {
            display = new URLSearchParams(item).get('keywords');
            if (display === "") {
                display = '   '
            }
        }
        if (index === 2) {
            item = <>
                <Stack direction={"row"} alignItems={"center"} gap={1}> <Flag code={item}
                                                                              height={"18"}/> {countries[item]}</Stack>
            </>
        }
        return (
            <PanelListItem updateUser={updateUser} subs={subs} open={open} key={`PanelListItem${index}-${itemIndex}`}
                           searchParams={searchParams}
                           setFavoriteSearchIndex={setFavoriteSearchIndex} favoriteSearchIndex={favoriteSearchIndex}
                           setUserSubscription={setUserSubscription}
                           handleClose={handleClose} setSearchParams={setSearchParams} index={index}
                           itemIndex={itemIndex} display={display}
                           item={item} removeItem={removeItem}/>);
    }

    const removeItem = useCallback((itemIndex, setLoading) => {
        let newData = [...data];
        let key;
        let listName;
        let listNameSingle;
        let query;
        if (index === 0) {
            key = "savedSearches";
            listName = "saved searches";
            listNameSingle = "saved search";
        } else if (index === 1) {
            key = "blockedSellers";
            listName = "blocked sellers";
            listNameSingle = "blocked seller";
        } else if (index === 2) {
            key = "blockedCountries";
            listName = "blocked countries";
            listNameSingle = "blocked country";
        }
        let deleted = newData.splice(itemIndex, 1)[0];
        if (key === 'savedSearches') {
            query = new URLSearchParams(deleted).toString();
            deleted = new URLSearchParams(deleted).get('keywords');
            if (deleted === '') {
                deleted = 'no keywords';
            }
        }
        if (index === 2) {
            deleted = countries[deleted];
        }
        updateUser({[key]: newData}, setLoading, `'${deleted}' deleted from ${listName}`, `Are you sure you want to delete '${deleted}' from ${listName}?`, "Delete", query ?? null, `Delete ${listNameSingle}`);
    }, [updateUser]);

    const subscriptionArray = useMemo(() => {
        if (!subscriptions) return [];
        return subscriptions.map((sub) => {
            if (sub.isDisabled) return;
            return sub.query;
        })
    }, [subscriptions]);

    return (
        <List sx={{height: '100%'}} ref={ref}>
            {loadingUserData ?
                [{}, {}, {}, {}, {}, {}, {}, {}].map((_, i) =>
                    <React.Fragment key={"loading-" + index + i}>
                        <ListItem sx={{padding: 0}} secondaryAction={
                            index === 0 ?
                                <Stack direction="row" spacing={2} justifyContent={"space-between"}>
                                    <Skeleton variant="circular" width={40} height={40}/>
                                </Stack>
                                :
                                <Skeleton variant="circular" width={40} height={40}/>
                                }>
                            <ListItemButton sx={{paddingLeft: "16px"}}>
                                <ListItemText sx={{padding: "0 16px"}}>
                                    {index === 0 ? <> <Skeleton width={"60%"}/> <Skeleton width={"35%"}/></> :
                                    <Skeleton width={"80%"}/>
                                    }
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                        <Divider sx={{marginLeft: "24px"}}/>
                    </React.Fragment>
                )
                :
                data.length > 0 ? data.map((i, index) => getListItem(i, index, subscriptionArray)) :
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={'100%'}>
                    <Typography sx={{color: 'lightgray'}}>{nullMessage}</Typography>
                </Box>}
        </List>
    );
}